/**
 * Minimal protobuf encoder for Biostream messages over the Meshtastic BLE transport.
 *
 * Wire format (both directions):
 *   ToRadio { packet: MeshPacket { to, decoded: Data { portnum=256, payload: BiostreamPayload } } }
 *
 * All exported encoders return hex strings ready for DataLabBridge.writeGatt().
 */

// ─── Primitives ───────────────────────────────────────────────────────────────

function encodeVarint(n: number): number[] {
  const bytes: number[] = [];
  while (n > 0x7f) {
    bytes.push((n & 0x7f) | 0x80);
    n >>>= 7;
  }
  bytes.push(n & 0x7f);
  return bytes;
}

function encodeTag(fieldNum: number, wireType: number): number[] {
  return encodeVarint((fieldNum << 3) | wireType);
}

function encodeStringField(fieldNum: number, value: string): number[] {
  if (!value) return [];
  const strBytes = Array.from(new TextEncoder().encode(value));
  return [...encodeTag(fieldNum, 2), ...encodeVarint(strBytes.length), ...strBytes];
}

function encodeSubMessage(fieldNum: number, msgBytes: number[]): number[] {
  if (msgBytes.length === 0) {
    // Even an empty sub-message needs the tag+length so the field is present
    return [...encodeTag(fieldNum, 2), 0x00];
  }
  return [...encodeTag(fieldNum, 2), ...encodeVarint(msgBytes.length), ...msgBytes];
}

function encodeFixed32LE(n: number): number[] {
  return [n & 0xff, (n >>> 8) & 0xff, (n >>> 16) & 0xff, (n >>> 24) & 0xff];
}

function toHex(bytes: number[]): string {
  return bytes.map((b) => b.toString(16).padStart(2, "0")).join("");
}

// ─── BiostreamPayload builder ─────────────────────────────────────────────────

function biostreamPayloadBytes(watchId: string, variantField: number, variantBytes: number[]): number[] {
  return [
    ...encodeStringField(1, watchId),                    // watch_id (omitted if empty)
    ...encodeSubMessage(variantField, variantBytes),      // variant field
  ];
}

// ─── Meshtastic ToRadio wrapper ───────────────────────────────────────────────
//
// Wraps raw BiostreamPayload bytes in:
//   ToRadio { packet (1): MeshPacket { to (2): fixed32, decoded (4): Data { portnum (1): 256, payload (2): bytes } } }
//
// toNodeNum defaults to 0xFFFFFFFF (broadcast) which is correct for DiscoverRequest.
// For targeted messages (PairConfirm etc.) pass the watch's node_num.

function wrapInToRadio(biostreamBytes: number[], toNodeNum: number = 0xffffffff): number[] {
  // Data { portnum=256 (field 1 varint), payload=bytes (field 2 bytes) }
  const dataBytes: number[] = [
    ...encodeTag(1, 0), ...encodeVarint(256),                                                 // portnum
    ...encodeTag(2, 2), ...encodeVarint(biostreamBytes.length), ...biostreamBytes,            // payload
  ];

  // MeshPacket { to=toNodeNum (field 2 fixed32), decoded=Data (field 4 msg) }
  const meshPacketBytes: number[] = [
    ...encodeTag(2, 5), ...encodeFixed32LE(toNodeNum),                                        // to
    ...encodeTag(4, 2), ...encodeVarint(dataBytes.length), ...dataBytes,                      // decoded
  ];

  // ToRadio { packet=MeshPacket (field 1 msg) }
  return [
    ...encodeTag(1, 2), ...encodeVarint(meshPacketBytes.length), ...meshPacketBytes,
  ];
}

// ─── Message encoders ─────────────────────────────────────────────────────────

/**
 * DiscoverRequest — broadcast to find all virtual watches on an emulator board.
 * watch_id is empty (broadcast). Meshtastic to=0xFFFFFFFF.
 */
export function encodeDiscoverRequest(): string {
  return toHex(wrapInToRadio(biostreamPayloadBytes("", 12, [])));
}

/**
 * PairConfirm — sent after medic assigns a soldier to a watch.
 */
export function encodePairConfirm(watchId: string): string {
  return toHex(wrapInToRadio(biostreamPayloadBytes(watchId, 4, [])));
}

/**
 * Unpair — remove a watch from the active event.
 */
export function encodeUnpair(watchId: string): string {
  return toHex(wrapInToRadio(biostreamPayloadBytes(watchId, 6, [])));
}

/**
 * StatusPoll — request a vitals snapshot. Empty watchId = broadcast to all.
 */
export function encodeStatusPoll(watchId: string): string {
  return toHex(wrapInToRadio(biostreamPayloadBytes(watchId, 10, [])));
}

/**
 * MedicInvite — sent over LoRa to invite a peer medic into the current field event.
 * Broadcasts to all nodes (to=0xFFFFFFFF). The recipient's app dispatches
 * a biostream:field-event-invite DOM event when it receives this.
 */
export function encodeMedicInvite(fieldEventId: string, hostName: string, hostUserId: string): string {
  const payload: number[] = [
    ...encodeStringField(1, fieldEventId),
    ...encodeStringField(2, hostName),
    ...encodeStringField(3, hostUserId),
  ];
  return toHex(wrapInToRadio(biostreamPayloadBytes("", 14, payload)));
}

/**
 * WatchEventRelay — re-broadcast a watch event over LoRa so peer medics receive
 * real-time updates without a WebSocket connection.
 * type is the event type string (e.g. "TRIAGE_ALERT"), triageLevel is the
 * raw proto enum value (0–5), or 0 if not applicable.
 */
export function encodeWatchEventRelay(
  watchId: string,
  opts: {
    type: string;
    soldierId: string;
    soldierName: string;
    triageLevel: number;
    batteryPct?: number;
    confidence?: number;
    latitudeI?: number;
    longitudeI?: number;
  },
): string {
  const payload: number[] = [
    ...encodeStringField(1, opts.type),
    ...encodeStringField(2, opts.soldierId),
    ...encodeStringField(3, opts.soldierName),
  ];
  if (opts.triageLevel) payload.push(...encodeTag(4, 0), ...encodeVarint(opts.triageLevel));
  if (opts.batteryPct  != null) payload.push(...encodeTag(6, 0), ...encodeVarint(opts.batteryPct));
  if (opts.confidence  != null) payload.push(...encodeTag(7, 0), ...encodeVarint(opts.confidence));
  if (opts.latitudeI   != null) payload.push(...encodeTag(8, 5), ...encodeFixed32LE(opts.latitudeI));
  if (opts.longitudeI  != null) payload.push(...encodeTag(9, 5), ...encodeFixed32LE(opts.longitudeI));
  return toHex(wrapInToRadio(biostreamPayloadBytes(watchId, 15, payload)));
}
